import {t, Trans} from '@lingui/macro';
import React, {FC, useEffect, useState} from 'react';
import styled from 'styled-components';
import {Key} from 'ts-key-enum';

import {HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {UserThemeConsumer} from '../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useDebounce} from '../../../srcCommon/helpers/hookHelpers';
import {useLocalize} from '../../../srcCommon/internationalization/internationalizationContext';
import {TabButtonElement} from '../../analytics/components/tabs/tabButtonElement';
import {ApplicationCardImageAvatar} from '../../components/common/applications/applicationCardImageAvatar';
import {IconTextBox} from '../../components/common/boxedInputs/iconTextBox';
import {useLinkNavigation} from '../../components/common/navigation/useLinkNavigation';
import {Skeleton} from '../../components/common/skeleton';
import {Pill} from '../../components/pill/pill';
import {renderIntegrationTypeCategoryName} from '../../helpers/integrationTypesHelpers';
import {IntegrationTypeModel} from '../../models/api/integrationTypesModel';
import {buildSettingsAppsAppDetailsUrl, buildSettingsAppsTabUrl} from '../../models/url/settingsUrlHelpers';
import {
  SettingsAppsAppDetailsTabsEnum,
  SettingsAppsAppDetailsUrl,
  SettingsAppsCategoriesUrl,
  SettingsAppsTabsEnum,
  SettingsAppsUrl,
} from '../../models/url/settingsUrlModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../services/tracking';
import {Localizer} from '../../store/stateless/internationalizationSelectors';
import {SettingsBreadcrumbs} from '../common/settingsBreadcrumbs';
import {SettingsHeader} from '../settingsHeader';
import {
  computeSettingsAppsTabFromPreviousUrl,
  renderSettingsAppsTabName,
} from './helpers/settingsAppsHelpers';
import {SettingsAppsHeaderActionContainer} from './settingsAppsHeaderActionContainer';

/*
 * Props.
 */

interface SettingsAppsHeaderProps {
  url: SettingsAppsUrl;
  integrationType?: IntegrationTypeModel;
  isIntegrationTypesLoading: boolean;
  isIntegrationTypeEnabled: boolean;
  companyUrl: string;
}

/*
 * Style.
 */

const StyledIconTextBox = styled(IconTextBox)`
  width: 264px;
`;

const StyledDetailsPageHeaderWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  gap: 8px;
  align-items: center;
`;

/*
 * Component.
 */

export const SettingsAppsHeader: FC<SettingsAppsHeaderProps> = (props) => {
  const {url, integrationType} = props;
  const {searchQuery} = url;

  const {navigateToLink} = useLinkNavigation();
  const localize = useLocalize();
  const [searchValue, setSearchValue] = useState(searchQuery || '');

  useEffect(() => {
    setSearchValue(searchQuery || '');
  }, [searchQuery]);

  const navigateToSearchQueryDebounced = useDebounce((value: string) => {
    trackInterfaceEvent(InterfaceEventsEnum.APPS_SETTINGS_SEARCH, {query: value});
    navigateToLink(buildSettingsAppsTabUrl(SettingsAppsTabsEnum.ALL, value));
  }, 250);

  const onSearchValueChanged = (value: string) => {
    navigateToSearchQueryDebounced(value);
    setSearchValue(value);
  };

  return (
    <SettingsHeader
      tabs={maybeRenderTabs(props, navigateToLink)}
      right={renderSettingsHeaderRightActions(
        url,
        searchValue,
        integrationType,
        onSearchValueChanged,
        localize,
        navigateToLink,
      )}
      topLine={maybeRenderBreadcrumbs(url, navigateToLink)}
    >
      {renderTitle(props)}
    </SettingsHeader>
  );
};

/*
 * Helpers.
 */

function renderSettingsHeaderRightActions(
  url: SettingsAppsUrl,
  searchValue: string,
  integrationType: IntegrationTypeModel | undefined,
  onSearchValueChanged: HandlerOf<string>,
  localize: Localizer,
  navigateToLink: HandlerOf<string>,
) {
  return (
    <>
      {maybeRenderSearchBar(url, searchValue, onSearchValueChanged, localize, navigateToLink)}
      <SettingsAppsHeaderActionContainer url={url} integrationType={integrationType} />
    </>
  );
}

function maybeRenderBreadcrumbs(url: SettingsAppsUrl, navigateToLink: HandlerOf<string>) {
  if (url.tabPage === SettingsAppsTabsEnum.CATEGORIES) {
    return renderCategoryBreadcrumbs(url, navigateToLink);
  }

  if (url.tabPage === SettingsAppsTabsEnum.DETAILS) {
    return renderDetailsBreadcrumbs(url, navigateToLink);
  }
  return null;
}

function renderDetailsBreadcrumbs(url: SettingsAppsAppDetailsUrl, navigateToLink: HandlerOf<string>) {
  const {previousUrl} = url;
  const previousUrlTab = computeSettingsAppsTabFromPreviousUrl(previousUrl || '');
  if (previousUrl && previousUrlTab) {
    return (
      <SettingsBreadcrumbs
        showSeparatorAfterTopLevel
        topLevelBreadcrumb={{
          label: renderSettingsAppsTabName(previousUrlTab),
          onBreadcrumbClick: () => navigateToLink(previousUrl),
        }}
        breadcrumbs={[]}
      />
    );
  }

  return (
    <SettingsBreadcrumbs
      showSeparatorAfterTopLevel
      topLevelBreadcrumb={{
        label: renderSettingsAppsTabName(SettingsAppsTabsEnum.COLLECTIONS),
        onBreadcrumbClick: () => navigateToLink(buildSettingsAppsTabUrl(SettingsAppsTabsEnum.COLLECTIONS)),
      }}
      breadcrumbs={[]}
    />
  );
}

function renderCategoryBreadcrumbs(url: SettingsAppsCategoriesUrl, navigateToLink: HandlerOf<string>) {
  // If we do not have a category selected, we do not need to render any breadcrumbs.
  if (!url.category) {
    return null;
  }

  const {previousUrl} = url;
  if (previousUrl && previousUrl.includes(SettingsAppsTabsEnum.COLLECTIONS)) {
    return (
      <SettingsBreadcrumbs
        showSeparatorAfterTopLevel
        topLevelBreadcrumb={{
          label: renderSettingsAppsTabName(SettingsAppsTabsEnum.COLLECTIONS),
          onBreadcrumbClick: () => navigateToLink(previousUrl),
        }}
        breadcrumbs={[]}
      />
    );
  }

  return (
    <SettingsBreadcrumbs
      showSeparatorAfterTopLevel
      topLevelBreadcrumb={{
        label: renderSettingsAppsTabName(SettingsAppsTabsEnum.CATEGORIES),
        onBreadcrumbClick: () => navigateToLink(buildSettingsAppsTabUrl(SettingsAppsTabsEnum.CATEGORIES)),
      }}
      breadcrumbs={[]}
    />
  );
}

function maybeRenderSearchBar(
  url: SettingsAppsUrl,
  searchValue: string,
  setSearchValue: HandlerOf<string>,
  localize: Localizer,
  navigateToLink: HandlerOf<string>,
) {
  if (
    url.tabPage === SettingsAppsTabsEnum.DETAILS ||
    (url.tabPage === SettingsAppsTabsEnum.CATEGORIES && url.category)
  ) {
    return null;
  }
  return (
    <StyledIconTextBox
      iconName="searchSmall"
      placeholder={localize(t('settings-apps-header-search-bar')`Search by app name or keyword`)}
      value={searchValue}
      onChange={setSearchValue}
      onKeyDown={(key) => {
        if (key.code === Key.Enter) {
          navigateToLink(buildSettingsAppsTabUrl(SettingsAppsTabsEnum.ALL, searchValue));
        }
      }}
    />
  );
}

function maybeRenderTabs(props: SettingsAppsHeaderProps, navigateToLink: HandlerOf<string>) {
  const {url, isIntegrationTypeEnabled} = props;

  if (url.tabPage === SettingsAppsTabsEnum.CATEGORIES && url.category) {
    return null;
  }

  if (url.tabPage === SettingsAppsTabsEnum.DETAILS) {
    if (!isIntegrationTypeEnabled) {
      return null;
    }
    return (
      <>
        <TabButtonElement
          name={<Trans id="settings-apps-header-details-overview">Overview</Trans>}
          isSelected={url.detailsPage === SettingsAppsAppDetailsTabsEnum.OVERVIEW}
          onTabClick={() =>
            navigateToLink(
              buildSettingsAppsAppDetailsUrl(
                url.appId,
                url.previousUrl,
                SettingsAppsAppDetailsTabsEnum.OVERVIEW,
              ),
            )
          }
        />
        <TabButtonElement
          name={<Trans id="settings-apps-header-details-getting-started">Getting started</Trans>}
          isSelected={url.detailsPage === SettingsAppsAppDetailsTabsEnum.GETTING_STARTED}
          onTabClick={() =>
            navigateToLink(
              buildSettingsAppsAppDetailsUrl(
                url.appId,
                url.previousUrl,
                SettingsAppsAppDetailsTabsEnum.GETTING_STARTED,
              ),
            )
          }
        />
      </>
    );
  }
  return (
    <>
      <TabButtonElement
        name={renderSettingsAppsTabName(SettingsAppsTabsEnum.COLLECTIONS)}
        isSelected={url.tabPage === SettingsAppsTabsEnum.COLLECTIONS}
        onTabClick={() => {
          trackInterfaceEvent(InterfaceEventsEnum.APPS_SETTINGS_CLICK, {
            id: SettingsAppsTabsEnum.COLLECTIONS,
          });
          navigateToLink(buildSettingsAppsTabUrl(SettingsAppsTabsEnum.COLLECTIONS));
        }}
      />
      <TabButtonElement
        name={renderSettingsAppsTabName(SettingsAppsTabsEnum.CATEGORIES)}
        isSelected={url.tabPage === SettingsAppsTabsEnum.CATEGORIES}
        onTabClick={() => {
          trackInterfaceEvent(InterfaceEventsEnum.APPS_SETTINGS_CLICK, {id: SettingsAppsTabsEnum.CATEGORIES});
          navigateToLink(buildSettingsAppsTabUrl(SettingsAppsTabsEnum.CATEGORIES));
        }}
      />
      <TabButtonElement
        name={renderSettingsAppsTabName(SettingsAppsTabsEnum.POPULAR)}
        isSelected={url.tabPage === SettingsAppsTabsEnum.POPULAR}
        onTabClick={() => {
          trackInterfaceEvent(InterfaceEventsEnum.APPS_SETTINGS_CLICK, {id: SettingsAppsTabsEnum.POPULAR});
          navigateToLink(buildSettingsAppsTabUrl(SettingsAppsTabsEnum.POPULAR));
        }}
      />
      <TabButtonElement
        name={renderSettingsAppsTabName(SettingsAppsTabsEnum.PLUGINS)}
        isSelected={url.tabPage === SettingsAppsTabsEnum.PLUGINS}
        onTabClick={() => {
          trackInterfaceEvent(InterfaceEventsEnum.APPS_SETTINGS_CLICK, {id: SettingsAppsTabsEnum.PLUGINS});
          navigateToLink(buildSettingsAppsTabUrl(SettingsAppsTabsEnum.PLUGINS));
        }}
      />
      <TabButtonElement
        name={renderSettingsAppsTabName(SettingsAppsTabsEnum.CHANNELS)}
        isSelected={url.tabPage === SettingsAppsTabsEnum.CHANNELS}
        onTabClick={() => {
          trackInterfaceEvent(InterfaceEventsEnum.APPS_SETTINGS_CLICK, {id: SettingsAppsTabsEnum.CHANNELS});
          navigateToLink(buildSettingsAppsTabUrl(SettingsAppsTabsEnum.CHANNELS));
        }}
      />
      <TabButtonElement
        name={renderSettingsAppsTabName(SettingsAppsTabsEnum.ENABLED)}
        isSelected={url.tabPage === SettingsAppsTabsEnum.ENABLED}
        onTabClick={() => {
          trackInterfaceEvent(InterfaceEventsEnum.APPS_SETTINGS_CLICK, {id: SettingsAppsTabsEnum.ENABLED});
          navigateToLink(buildSettingsAppsTabUrl(SettingsAppsTabsEnum.ENABLED));
        }}
      />
      <TabButtonElement
        name={renderSettingsAppsTabName(SettingsAppsTabsEnum.ALL)}
        isSelected={url.tabPage === SettingsAppsTabsEnum.ALL}
        onTabClick={() => {
          trackInterfaceEvent(InterfaceEventsEnum.APPS_SETTINGS_CLICK, {id: SettingsAppsTabsEnum.ALL});
          navigateToLink(buildSettingsAppsTabUrl(SettingsAppsTabsEnum.ALL));
        }}
      />
    </>
  );
}

function renderTitle(props: SettingsAppsHeaderProps) {
  const {url} = props;

  switch (url.tabPage) {
    case SettingsAppsTabsEnum.DETAILS:
      return renderDetailsTitle(props);
    case SettingsAppsTabsEnum.CATEGORIES: {
      if (url.category) {
        return renderIntegrationTypeCategoryName(url.category);
      }
      return renderDefaultTitle();
    }
    default:
      return renderDefaultTitle();
  }
}

function renderDefaultTitle() {
  return <Trans id="settings-apps-header-title">App store</Trans>;
}

function renderDetailsTitle(props: SettingsAppsHeaderProps) {
  const {integrationType, isIntegrationTypesLoading, companyUrl, isIntegrationTypeEnabled} = props;

  if (isIntegrationTypesLoading) {
    return (
      <StyledDetailsPageHeaderWrapperDiv>
        <Skeleton height="24px" width="24px" borderRadius="150px" />
        <Skeleton height="31px" width="200px" />
      </StyledDetailsPageHeaderWrapperDiv>
    );
  }
  if (!integrationType) {
    return <Trans id="settings-apps-header-invalid-integration-title">Invalid app</Trans>;
  }
  return (
    <StyledDetailsPageHeaderWrapperDiv>
      <ApplicationCardImageAvatar
        url={`${companyUrl}/integration_types/${integrationType.id}/public/icons/`}
        size={VisualSizesEnum.SMALL}
      />
      {integrationType.name}
      {isIntegrationTypeEnabled && (
        <UserThemeConsumer>
          {(theme) => (
            <Pill
              backgroundColor={theme.palette.green.shade20}
              color={theme.palette.green.shade40}
              size={VisualSizesEnum.MEDIUM}
            >
              <Trans id="settings-apps-header-enabled-pill">Enabled</Trans>
            </Pill>
          )}
        </UserThemeConsumer>
      )}
    </StyledDetailsPageHeaderWrapperDiv>
  );
}
